/**
 * load-more.js
 * Web component: <load-more-button>
 *
 * - Appends products to the grid one page at a time ("Load more")
 * - Loads all remaining pages sequentially ("View all")
 * - Builds and syncs the toolbar pagination (#toolbar-pagination)
 *
 * Required attributes:
 *   grid-id       — id of the <ul> product grid to append into
 *   next-url      — URL of the next paginated page (e.g. ?page=2)
 *   current-page  — the page number currently rendered server-side
 *   total-pages   — total number of pages
 *   collection-url — base collection URL for building page hrefs
 */
class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this._loading = false;
    this.button = this.querySelector('[data-load-more-btn]');
    this.viewAllLink = this.querySelector('[data-view-all]');

    this.button?.addEventListener('click', () => this._load());
    this.viewAllLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this._loadAll();
    });

    this._buildToolbarPagination();
  }

  get _gridId() { return this.getAttribute('grid-id'); }
  get _nextUrl() { return this.getAttribute('next-url'); }
  set _nextUrl(url) { this.setAttribute('next-url', url); }
  get _totalPages() { return parseInt(this.getAttribute('total-pages'), 10); }
  get _currentPage() { return parseInt(this.getAttribute('current-page'), 10); }
  set _currentPage(n) { this.setAttribute('current-page', String(n)); }
  get _collectionUrl() { return this.getAttribute('collection-url'); }

  // Build the page number buttons in the toolbar nav
  _buildToolbarPagination() {
    const nav = document.getElementById('toolbar-pagination');
    if (!nav || this._totalPages < 2) return;

    nav.innerHTML = '';
    for (let i = 1; i <= this._totalPages; i++) {
      const a = document.createElement('a');
      a.href = `${this._collectionUrl}?page=${i}`;
      a.className = 'collection-toolbar__page-btn' + (i === this._currentPage ? ' is-active' : '');
      a.setAttribute('aria-label', `Page ${i}`);
      if (i === this._currentPage) a.setAttribute('aria-current', 'page');
      a.dataset.page = i;
      a.textContent = i;

      // Intercept clicks — load up to that page instead of navigating
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this._loadUpTo(parseInt(a.dataset.page, 10));
      });

      nav.appendChild(a);
    }
  }

  // Update active state on toolbar buttons up to the given page
  _syncToolbarPagination(loadedPage) {
    document.querySelectorAll('#toolbar-pagination .collection-toolbar__page-btn').forEach((btn) => {
      const btnPage = parseInt(btn.dataset.page, 10);
      const isActive = btnPage === loadedPage;
      btn.classList.toggle('is-active', isActive);
      isActive ? btn.setAttribute('aria-current', 'page') : btn.removeAttribute('aria-current');
    });
  }

  async _fetchPage(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    const allGrids = document.querySelectorAll('.product-grid');
    const grid = allGrids[allGrids.length - 1];
    // The fetched page's grid may be split into multiple <ul class="product-grid">
    // chunks when breakout blocks are injected between products. Only the first
    // chunk carries the grid id, so collect <li>s from every chunk to avoid
    // dropping products that appear after a breakout on the next page.
    const newGrids = doc.querySelectorAll('.product-grid');
    if (grid && newGrids.length) {
      newGrids.forEach((ul) => {
        [...ul.children].forEach((item) => grid.appendChild(item));
      });
    }

    const nextPage = this._currentPage + 1;
    this._currentPage = nextPage;
    this._syncToolbarPagination(nextPage);

    const nextComponent = doc.querySelector('load-more-button');
    return nextComponent ? nextComponent.getAttribute('next-url') : null;
  }

  async _load() {
    if (this._loading || !this._nextUrl) return;
    this._setLoadingState(true);

    try {
      const nextUrl = await this._fetchPage(this._nextUrl);
      if (!nextUrl || this._currentPage >= this._totalPages) {
        this.remove();
      } else {
        this._nextUrl = nextUrl;
        this._setLoadingState(false);
      }
    } catch (err) {
      console.error('[load-more] Fetch failed:', err);
      this._setLoadingState(false);
    }
  }

  async _loadAll() {
    if (this._loading) return;
    this._setLoadingState(true, true);

    try {
      let nextUrl = this._nextUrl;
      while (nextUrl && this._currentPage < this._totalPages) {
        nextUrl = await this._fetchPage(nextUrl);
      }
      this.remove();
    } catch (err) {
      console.error('[load-more] View all failed:', err);
      this._setLoadingState(false, true);
    }
  }

  // Load all pages up to (and including) targetPage
  async _loadUpTo(targetPage) {
    if (this._loading || targetPage <= this._currentPage) return;
    this._setLoadingState(true);

    try {
      let nextUrl = this._nextUrl;
      while (nextUrl && this._currentPage < targetPage) {
        nextUrl = await this._fetchPage(nextUrl);
      }
      if (!nextUrl || this._currentPage >= this._totalPages) {
        this.remove();
      } else {
        this._nextUrl = nextUrl;
        this._setLoadingState(false);
      }
    } catch (err) {
      console.error('[load-more] Load to page failed:', err);
      this._setLoadingState(false);
    }
  }

  _setLoadingState(on, viewAllMode = false) {
    this._loading = on;
    if (this.button) this.button.disabled = on;
    if (this.viewAllLink) {
      this.viewAllLink.toggleAttribute('aria-disabled', on);
      if (viewAllMode) this.viewAllLink.toggleAttribute('aria-busy', on);
    }
    if (!viewAllMode && this.button) {
      this.button.toggleAttribute('aria-busy', on);
    }
  }
}

customElements.define('load-more-button', LoadMoreButton);
